import { Fragment } from "react";
import { styled } from "styled-components";

import { PlusIcon, TrashIcon } from "@vericus/cadmus-icons";
import {
  Button,
  Color,
  ControlButton,
  Input,
  LinkButton,
  Spacer,
  Text,
  vars,
} from "@vericus/cadmus-ui";

import copy from "copy-to-clipboard";

import { AccessCodeFragment, AccessCodeInput } from "@/generated/graphql";
import { useDebouncedDispatch } from "@/utils/useDebouncedDispatch";

interface Props {
  /// list of AccessCodes
  accessCodes: AccessCodeFragment[];

  /// update access code (modify or delete)
  updateAccessCode: (input: AccessCodeInput) => void;

  /// callback for generating a randomised new access code
  generateNewAccessCode: VoidFunction;

  /// Prevent deletion and generation of Access Code
  disabled: boolean;
}

export function AccessCodeList({
  accessCodes,
  updateAccessCode,
  generateNewAccessCode,
  disabled,
}: Props) {
  const copyAllCodes = () => {
    const codes = accessCodes
      .map((item) => `${item.label}\t${item.code}`)
      .join("\n");
    copy(codes, { format: "text/plain" });
  };

  return (
    <>
      <Grid>
        <Text kind="label" color="grey600" as="div">
          Code
        </Text>
        <Text kind="label" color="grey600" as="div">
          Label
        </Text>
        {accessCodes.map((item, index) => (
          <Fragment key={item.code}>
            <CodeText code={item.code} />
            <Column>
              <LabelInput
                label={item.label ?? ""}
                onUpdate={(label) =>
                  updateAccessCode({
                    code: item.code,
                    active: item.active,
                    label,
                  })
                }
              />
              <ControlButton
                iconName="Copy"
                onClick={() =>
                  copy(`${item.label}\t${item.code}`, {
                    format: "text/plain",
                  })
                }
              />
              {index > 0 && !disabled && (
                <ControlButton
                  icon={<TrashIcon size={24} label="Delete access code" />}
                  onClick={() =>
                    updateAccessCode({
                      code: item.code,
                      label: item.label ?? "",
                      active: false,
                    })
                  }
                />
              )}
            </Column>
          </Fragment>
        ))}
      </Grid>
      <Spacer spacing={12} />
      <FooterButtons
        disabled={disabled}
        copyAllCodes={copyAllCodes}
        generateNewAccessCode={generateNewAccessCode}
      />
    </>
  );
}

interface LabelInputProps {
  label: string;
  onUpdate: (label: string) => void;
}

function LabelInput(props: LabelInputProps) {
  const [value, setValue] = useDebouncedDispatch(
    props.label,
    props.onUpdate,
    400
  );
  return (
    <Input
      placeholder="Access code label"
      value={value}
      maxLength={20}
      error={value.length === 0}
      onChange={(e) => {
        setValue(e.target.value);
      }}
    />
  );
}

interface FooterButtonsProps {
  generateNewAccessCode: VoidFunction;
  copyAllCodes: VoidFunction;
  disabled: boolean;
}

function FooterButtons(props: FooterButtonsProps) {
  const { generateNewAccessCode, disabled, copyAllCodes } = props;
  return (
    <FooterButtonContainer>
      {disabled === false && (
        <Button
          kind="outline"
          size="md"
          onClick={generateNewAccessCode}
          icon={<PlusIcon size={24} label="" />}
        >
          Generate more
        </Button>
      )}
      <LinkButton onClick={copyAllCodes} kind="primary">
        Copy all codes
      </LinkButton>
    </FooterButtonContainer>
  );
}

interface CodeTextProps {
  code: string;
  bgColor?: Color;
  size?: number;
}

export const CodeText = (props: CodeTextProps) => {
  return (
    <Container>
      {props.code.split("").map((char, index) => (
        <CodeLetter key={index} bgColor={props.bgColor} size={props.size}>
          {char}
        </CodeLetter>
      ))}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 8px;
`;

const Grid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr 1fr;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const CodeLetter = styled.div<{
  bgColor?: Color;
  size?: number;
}>`
  --size: ${(p) => p.size ?? 26}px;
  width: var(--size);
  height: var(--size);
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  line-height: 1.5;
  border-radius: 2px;
  color: ${vars.colors.blue800};
  background: ${(p) =>
    p.bgColor ? vars.colors[p.bgColor] : "hsla(210, 45%, 96%, 0.5)"};
  border: 0;
`;

const Column = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FooterButtonContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;
